import React, { ForwardRefRenderFunction, MutableRefObject } from "react";
import { Button, Chip } from "@mui/material";
import { Form } from "react-final-form";

import { useUpdateNavigationElementMutation } from "@/features/admin/features/pages-management";
import {
  Modal,
  ModalComponent,
  TextField,
  ModalActions,
  ModalTitle,
  ModalContent,
  showToast,
  Select,
} from "@/features/shared/components";
import { NavigationElement } from "@/lib/api";
import { useAdminDashboard } from "@/features/admin/hooks";
import IconSelector from "@/features/admin/components/icon-selector/icon-selector";
import { useTranslation } from "@/lib/i18n";

type Props = {
  initialValues: NavigationElement;
  onClose?: () => void;
};

/**
 * Dialog for the page and folder settings, can be opened from the pages overview or from a detail page.
 */
const NavigationElementSettings: ForwardRefRenderFunction<ModalComponent, Props> = (
  { initialValues, onClose },
  ref,
) => {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const updateNavigationElementMutation = useUpdateNavigationElementMutation();

  const modalRef = ref as MutableRefObject<ModalComponent>;

  const isFolder = !initialValues.page_id;

  async function onSubmit(values: NavigationElement) {
    updateNavigationElementMutation.mutate(
      {
        dashboardId: dashboard.id,
        navigationElementId: values.id,
        data: {
          title: values.title,
          icon: values.icon,
        },
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.page_updated"));
          modalRef.current?.close();
        },
      },
    );
  }

  return (
    <Modal ref={modalRef} onClose={() => onClose?.()}>
      <Form<NavigationElement>
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <>
            <ModalTitle>
              {t("admin.pages.page_settings_dialog.title", {
                type: isFolder ? "Map" : "Pagina",
              })}
            </ModalTitle>
            <ModalContent>
              <form onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="dense"
                  name="title"
                  label={t("admin.pages.page_settings_dialog.name_field", {
                    type: isFolder ? "Map" : "Pagina",
                  })}
                  required
                />
                {values.title !== initialValues.title && (
                  <Chip
                    sx={{ mt: 2 }}
                    variant="outlined"
                    label={t("admin.pages.page_settings_dialog.slug_changed_message")}
                    color="primary"
                  />
                )}

                <Select
                  label="Icon"
                  options={[
                    {
                      label: "Symbol",
                      value: "symbol",
                    },
                    {
                      label: "Abbreviation",
                      value: "abbreviation",
                    },
                  ]}
                  name="icon.type"
                />

                {values.icon?.type === "symbol" && <IconSelector name="icon.variant" />}
                {values.icon?.type === "abbreviation" && (
                  <TextField
                    margin="dense"
                    name="icon.content"
                    placeholder="Abbreviation"
                    inputProps={{ maxLength: 2 }}
                    required={values.icon?.type === "abbreviation"}
                  />
                )}
              </form>
            </ModalContent>
            <ModalActions>
              <Button key="cancel" onClick={() => modalRef.current?.close()}>
                {t("general.cancel")}
              </Button>
              <Button key="save" onClick={handleSubmit}>
                {t("general.save")}
              </Button>
            </ModalActions>
          </>
        )}
      />
    </Modal>
  );
};

export default React.forwardRef(NavigationElementSettings);
